import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { BellAlertIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { invitationApi } from '@/services/roleApi';
import { useAuth } from '@/hooks/useAuth';

interface PendingInvite {
  token: string;
  organizationName: string;
  roleName: string;
  inviterName: string;
  expiresAt: string;
  message?: string;
}

const PendingInviteBanner: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const { data } = useQuery({
    queryKey: ['pending-invites-for-me'],
    queryFn: () => invitationApi.getPendingForMe().then(res => res.data.data),
    enabled: isAuthenticated,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const acceptMutation = useMutation({
    mutationFn: (token: string) => invitationApi.acceptAsMember(token),
    onSuccess: (res, token) => {
      toast.success(res.data.message || 'Successfully joined organisation!');
      setDismissed(prev => new Set(prev).add(token));
      queryClient.invalidateQueries({ queryKey: ['pending-invites-for-me'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      navigate('/app/organization');
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to join organisation'),
  });

  const visibleInvites = (data || []).filter((inv: PendingInvite) => !dismissed.has(inv.token));

  if (!visibleInvites.length) return null;

  return (
    <div className="mb-4 space-y-2">
      {visibleInvites.map((inv: PendingInvite) => (
        <div
          key={inv.token}
          className="flex items-start gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg"
        >
          <BellAlertIcon className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-indigo-900">
              You've been invited to join <strong>{inv.organizationName}</strong> as{' '}
              <span className="text-indigo-700">{inv.roleName}</span>
            </p>
            <p className="text-xs text-indigo-700 mt-0.5">Invited by {inv.inviterName}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => navigate(`/accept-invitation/${inv.token}`)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            >
              <CheckIcon className="h-3.5 w-3.5" />
              Join
            </button>
            <button
              onClick={() => setDismissed(prev => new Set(prev).add(inv.token))}
              className="p-1 text-indigo-400 hover:text-indigo-600 rounded"
              title="Dismiss"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingInviteBanner;
